import { twMerge } from 'tailwind-merge';
import DrawerOffer from './DrawerOffer';
import { TwButton } from 'components/TwButton/TwButton';
import CloseIcon from 'assets/icons/close.svg?react';
import Sliders from 'components/Sliders/Sliders';
import useCalculator from 'hooks/calculator/useCalculator';
import { useConstraints } from 'hooks/context';
import { useTranslation } from 'react-i18next';
import { format } from 'utils/format';

interface DrawerProps {
  drawerOpen: boolean;
  handleConfirm: () => void;
  handleCancel: () => void;
}

export default function Drawer({
  drawerOpen,
  handleConfirm,
  handleCancel,
}: DrawerProps) {
  const { t } = useTranslation();
  const { amount, handleAmountChange, offer } = useCalculator();
  const { constraints } = useConstraints();

  return (
    <div
      data-test="drawer"
      className={twMerge(
        'absolute bg-background px-8 pt-5 pb-20 flex flex-col w-full z-10 transition-transform',
        'left-0 right-0 border-b -translate-y-full',
        'lg:right-auto lg:border-b-0 lg:border-r lg:w-80 lg:h-full lg:min-h-full lg:top-0 lg:-translate-x-full lg:pt-11 lg:-translate-y-0',
        drawerOpen && 'translate-y-0',
        drawerOpen && 'lg:translate-x-0',
      )}
    >
      <button
        type="button"
        onClick={handleCancel}
        className="hidden lg:absolute lg:w-10 lg:h-10 lg:flex lg:justify-center lg:items-center lg:top-2 lg:right-2"
        data-test="drawer-btn"
      >
        <span className="sr-only" data-test="drawer-close-text">
          {t('calculator:close')}
        </span>
        <CloseIcon width="16px" height="16px" data-test="drawer-close-icon" />
      </button>
      <div className="hidden lg:block  border-b pb-4 ">
        <div
          className="leading-5 font-medium text-lg pb-[18px]"
          data-test="drawer-edit"
        >
          {t('calculator:edit')}
        </div>
      </div>
      <div className="lg:my-5 flex flex-col gap-4">
        <Sliders
          constraints={constraints}
          onAmountChange={handleAmountChange}
          amount={amount}
          dataTest="drawer-slider"
        />

        <DrawerOffer
          amount={offer?.totalRepayableAmount}
          dueDate={offer?.agreementEndDate}
        />

        <div
          className="text-xs text-muted"
          data-test="drawer-calculator-example"
        >
          {t('calculator:dynamicLoanDescription', {
            loanAmount: format.number(amount, 'amount'),
            interestRate: format.number(offer?.interestRate, 'decimal'),
            annualPercentageRate: format.number(
              offer?.annualPercentageRate,
              'decimal',
            ),
            totalRepayableAmount: format.number(
              offer?.totalRepayableAmount,
              'amount',
            ),
            totalInterest: format.number(offer?.totalInterest, 'amount'),
          })}
        </div>

        <div
          className="text-xs text-muted"
          data-test="drawer-calculator-example-explanation"
        >
          {t('calculator:dynamicLoanDescriptionExplanation')}
        </div>

        <TwButton
          className="my-5 mx-auto bg-primary"
          disabled={!constraints}
          onClick={handleConfirm}
        >
          {t('common:confirm')}
        </TwButton>
      </div>
    </div>
  );
}
